import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { WarningCircle } from "@phosphor-icons/react";
import { parseMarketSize } from "@/lib/marketSize";
import { senseCheckMarketSizes } from "@/lib/marketValue";

/**
 * Structured Market-Size editor for slide 4.
 *
 * Iter53 (Feb 2026) replaces the free-form content textarea with three
 * dedicated {size, reference} inputs — one per ring. The three rings render
 * in fixed canonical order (TAM → SAM → SOM) so the preview always builds
 * the correct nested funnel.
 *
 * Iter54 (Feb 2026): moved edits into LOCAL state so trailing spaces &
 * special characters stick while typing. Round-tripping through
 * `assembleContent` → `content` → `parseMarketSize` was stripping trailing
 * whitespace via `.trim()`, blocking the spacebar mid-word. External content
 * updates (e.g. AI rewrite) still reconcile via a ref-guarded effect.
 *
 * Content persistence: reuses the same `slide.content` string field that
 * every other slide uses, formatted in the shape `parseMarketSize` already
 * understands (LABEL — value / description). This keeps the PPTX exporter,
 * Play deck, and premium-rewrite flow all working without changes.
 *
 * Sense-check: SOM ≤ SAM ≤ TAM (see `parseMarketValue`). Silent while the
 * user is typing partial input; fires only when all relevant values parse.
 */

const RINGS = [
  {
    key: "TAM",
    label: "Total Addressable Market",
    sizePlaceholder: "e.g. 5 million SMEs · USD 2bn Kenyan fast food",
    refPlaceholder: "e.g. World Bank 2024 · IFC SME Finance Forum",
    dot: "primary",
  },
  {
    key: "SAM",
    label: "Serviceable Addressable Market",
    sizePlaceholder: "e.g. 1.5 million SMEs · $600M formal food sector",
    refPlaceholder: "e.g. KNBS 2024 · McKinsey Africa report",
    dot: "primaryLight",
  },
  {
    key: "SOM",
    label: "Serviceable Obtainable Market",
    sizePlaceholder: "e.g. 500K SMEs · $60M targetable in 3 years",
    refPlaceholder: "e.g. Clinq internal projection 2025",
    dot: "gold",
  },
];

const C = {
  border: "rgba(255,255,255,0.08)",
  ink: "#F5F7FA",
  muted: "rgba(255,255,255,0.55)",
  bg: "#0F1B22",
  bgSoft: "#132029",
  danger: "#F87171",
};

/** Assemble the three ring inputs into the canonical content string
 *  format that `parseMarketSize` already round-trips.
 *  NOTE: we intentionally do NOT trim `size`/`ref` here — that was the
 *  root cause of the spacebar bug. Trimming happens once at persistence
 *  layer if it needs to. */
function assembleContent(sizes, refs) {
  const blocks = [];
  for (const r of RINGS) {
    const size = sizes[r.key] || "";
    const ref = refs[r.key] || "";
    // Skip a ring only when both fields are fully empty. A ring with only
    // whitespace is treated as empty for content-string purposes.
    if (!size.trim() && !ref.trim()) continue;
    const head = size ? `${r.key} — ${size}` : `${r.key}`;
    blocks.push(ref ? `${head}\n${ref}` : head);
  }
  return blocks.join("\n\n");
}

function parseToLocal(content) {
  const rings = parseMarketSize(content || "");
  const byKey = { TAM: null, SAM: null, SOM: null };
  for (const r of rings) byKey[r.label] = r;
  return {
    sizes: {
      TAM: byKey.TAM?.value || "",
      SAM: byKey.SAM?.value || "",
      SOM: byKey.SOM?.value || "",
    },
    refs: {
      TAM: byKey.TAM?.description || "",
      SAM: byKey.SAM?.description || "",
      SOM: byKey.SOM?.description || "",
    },
  };
}

export default function MarketSizeForm({ content, onChange, primary }) {
  // Local state owns the input values so trailing spaces stick.
  const [sizes, setSizes] = useState(() => parseToLocal(content).sizes);
  const [refs, setRefs] = useState(() => parseToLocal(content).refs);
  // Track the last content string we emitted upward so we can distinguish
  // an external change (e.g. AI rewrite loaded a new draft) from our own
  // just-emitted change echoing back through props.
  const lastEmittedRef = useRef(content || "");

  useEffect(() => {
    if ((content || "") === lastEmittedRef.current) return;
    // External change — reconcile local state from the new content.
    const next = parseToLocal(content);
    setSizes(next.sizes);
    setRefs(next.refs);
    lastEmittedRef.current = content || "";
  }, [content]);

  const issues = useMemo(() => senseCheckMarketSizes(sizes), [sizes]);

  const update = useCallback((key, field, value) => {
    const nextSizes = field === "size" ? { ...sizes, [key]: value } : sizes;
    const nextRefs = field === "ref" ? { ...refs, [key]: value } : refs;
    if (field === "size") setSizes(nextSizes);
    else setRefs(nextRefs);
    const assembled = assembleContent(nextSizes, nextRefs);
    lastEmittedRef.current = assembled;
    onChange(assembled);
  }, [sizes, refs, onChange]);

  const dotColor = (dot) => {
    if (dot === "primary") return primary;
    if (dot === "primaryLight") return `${primary}CC`;
    return "#F4C33A";
  };

  return (
    <div data-testid="market-size-form" className="space-y-4"
      style={{ fontFamily: "'Raleway', 'Manrope', system-ui, sans-serif" }}>
      <div className="p-3 rounded-md text-[13px] leading-relaxed"
        style={{ backgroundColor: `${primary}14`, border: `1px solid ${primary}33`, color: C.ink,
                 fontFamily: "'Raleway', 'Manrope', system-ui, sans-serif" }}>
        <strong className="font-extrabold">Market Size</strong> uses a canonical nested-funnel visual. Fill in the
        size and cite a source for each ring — the preview updates live and the layout is locked so the
        design stays clean across the deck.
      </div>

      {issues.length > 0 && (
        <div
          data-testid="market-size-sensecheck-alert"
          className="p-3 rounded-md flex items-start gap-2 text-[13px] leading-relaxed"
          style={{ backgroundColor: "rgba(248, 113, 113, 0.10)", border: `1px solid ${C.danger}55`, color: "#FCA5A5" }}
        >
          <WarningCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            {issues.map((iss, i) => <div key={i}>{iss.message}</div>)}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {RINGS.map((r) => (
          <div key={r.key} data-testid={`market-size-row-${r.key}`}
            className="p-4 rounded-lg"
            style={{ backgroundColor: C.bgSoft, border: `1px solid ${C.border}` }}>
            <div className="flex items-center gap-2.5 mb-3">
              <span className="inline-flex items-center justify-center rounded-full font-extrabold text-[12.5px]"
                style={{ width: 26, height: 26, backgroundColor: dotColor(r.dot), color: r.dot === "gold" ? "#0B1F1E" : "#fff" }}>
                {r.key[0]}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-extrabold uppercase tracking-[0.20em]" style={{ color: dotColor(r.dot) }}>
                  {r.key}
                </div>
                <div className="text-[13px] font-bold" style={{ color: C.ink }}>
                  {r.label}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              <label className="block">
                <span className="block text-[11px] font-extrabold uppercase tracking-[0.16em] mb-1" style={{ color: C.muted }}>
                  Size of market
                </span>
                <input
                  type="text"
                  data-testid={`market-size-${r.key}-size`}
                  value={sizes[r.key]}
                  onChange={(e) => update(r.key, "size", e.target.value)}
                  placeholder={r.sizePlaceholder}
                  className="w-full px-3 py-2.5 rounded text-[15px] focus:outline-none"
                  style={{ backgroundColor: C.bg, border: `1px solid ${C.border}`, color: C.ink,
                           fontFamily: "'Raleway', 'Manrope', system-ui, sans-serif",
                           fontWeight: 500 }}
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-extrabold uppercase tracking-[0.16em] mb-1" style={{ color: C.muted }}>
                  Reference / source
                </span>
                <input
                  type="text"
                  data-testid={`market-size-${r.key}-ref`}
                  value={refs[r.key]}
                  onChange={(e) => update(r.key, "ref", e.target.value)}
                  placeholder={r.refPlaceholder}
                  className="w-full px-3 py-2.5 rounded text-[15px] focus:outline-none"
                  style={{ backgroundColor: C.bg, border: `1px solid ${C.border}`, color: C.ink,
                           fontFamily: "'Raleway', 'Manrope', system-ui, sans-serif",
                           fontWeight: 500 }}
                />
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
